import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { NavigationService, SolNavbarItem, SolRouteId, SolText, TranslatePipe } from '@sol/components';
import { LabIconComponent } from '../lab-icon/lab-icon.component';
import { LabBadgeComponent } from '../lab-badge/lab-badge.component';
import { NavigationTarget } from '@sol/components/tools/router-tools';
import { NgTemplateOutlet } from '@angular/common';
import { SolNavbarItemState } from './lab-navbar-item.state';

export interface SolNavbarExpandedEvent {
  id: SolRouteId;
  expanded: boolean;
}

@Component({
  selector: 'lab-navbar-item',
  standalone: true,
  imports: [LabBadgeComponent, LabIconComponent, TranslatePipe, NgTemplateOutlet],
  templateUrl: './lab-navbar-item.component.html',
  styles: ['lab-navbar-item { display: block; }'],
  encapsulation: ViewEncapsulation.None,
})
export class LabNavbarItemComponent extends AbstractComponent<SolNavbarItemState> {
  // Dependencies
  protected readonly navigationService = inject(NavigationService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // Computed
  protected readonly text$ = this.compute(state => state.text);
  protected readonly prependIcon$ = this.compute(state => state.prependIcon);
  protected readonly appendIcon$ = this.compute(state => state.appendIcon);
  protected readonly isMenuGroup$ = this.compute(state => state.isMenuGroup);
  protected readonly isTopLevelGroup$ = this.compute(state => state.isTopLevelGroup);
  protected readonly expanded$ = this.compute(state => state.expanded);
  protected readonly expandedIds$ = this.compute(state => state.expandedIds);
  protected readonly children$ = this.compute(state => state.children);
  protected readonly level$ = this.compute(state => state.level);
  protected readonly buttonClasses$ = this.compute(state => state.buttonClasses);
  protected readonly activeRouteIds$ = this.compute(state => state.activeRouteIds);
  protected readonly isNew$ = this.compute(state => state.isNew);
  protected readonly showBadge$ = this.compute(state => state.showBadge);
  protected readonly newBadgeLabel = SolText.ofRaw('New');
  protected readonly modifiedBadgeLabel = SolText.ofRaw('Modified');

  // Events
  @Output('expandedChange')
  private readonly expandedChangeEmitter = new EventEmitter<SolNavbarExpandedEvent>();

  constructor() {
    super(SolNavbarItemState.of());
  }

  @HostBinding('class')
  public get hostClass(): string {
    return 'lab-navbar-item';
  }

  @Input()
  public set item(item: SolNavbarItem) {
    this.update(state => state.withItem(item));
  }

  @Input()
  public set activeRouteIds(activeRouteIds: SolRouteId[]) {
    this.update(state => state.withActiveRouteIds(activeRouteIds));
  }

  @Input()
  public set expandedIds(expandedIds: SolRouteId[]) {
    this.update(state => state.withExpandedIds(expandedIds));
  }

  @Input()
  public set level(level: number) {
    this.update(state => state.withLevel(level));
  }

  protected onClick(event: MouseEvent, newTab: boolean = false): void {
    event.stopPropagation();

    const state = this.state;
    if (state.item.isMenuGroup) {
      const expanded = !state.expandedIds.includes(state.item.id);
      this.expandedChangeEmitter.emit({
        id: state.item.id,
        expanded,
      });
      if (expanded) {
        this.scrollExpandedIntoView();
      }
    } else if (state.selected) {
      return;
    } else {
      this.navigationService.navigate(state.item.id, {
        target: newTab ? NavigationTarget.NewTab : NavigationTarget.SameTab,
      });
    }
  }

  private scrollExpandedIntoView(): void {
    const wrapper = this.elementRef.nativeElement.querySelector<HTMLElement>(
      ':scope > .lab-navbar-menu-group-items-wrapper',
    );
    if (!wrapper) return;
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'grid-template-rows') return;
      wrapper.removeEventListener('transitionend', onEnd);
      wrapper.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    wrapper.addEventListener('transitionend', onEnd);
  }

  protected onExpanded(event: SolNavbarExpandedEvent) {
    this.expandedChangeEmitter.emit(event);
  }
}
